import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
} from "react";
import { getNotificacionesAmistad, getUnreadMessages } from "../services/api";
import { useAuth } from "../auth/AuthContext";
import { useWebSocketActions } from "../context/WebSocketContext";
import { toast } from "react-hot-toast";
import { useTranslation } from "react-i18next";

const NotificationContext = createContext();

export const useNotification = () => useContext(NotificationContext);

export const NotificationProvider = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const { addListener } = useWebSocketActions();
  const { t } = useTranslation();

  const [notificaciones, setNotificaciones] = useState(0);
  const [unreadBySender, setUnreadBySender] = useState([]);
  const [activeChatUserId, setActiveChatUserId] = useState(null);

  const notify = (message, type = "default") => {
    switch (type) {
      case "success":
        toast.success(message);
        break;
      case "error":
        toast.error(message);
        break;
      case "loading":
        toast.loading(message);
        break;
      default:
        toast(message);
    }
  };

  const fetchNotificaciones = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      const res = await getNotificacionesAmistad();
      setNotificaciones(res.data.pendientes || 0);
    } catch (err) {
      if (err.response?.status !== 401) {
        console.error("❌ Error al cargar notificaciones de amistad:", err);
      }
    }
  }, [isAuthenticated]);

  const fetchUnreadMessages = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      const res = await getUnreadMessages();
      let unread = res.data || [];

      if (activeChatUserId) {
        unread = unread.filter(
          (u) => String(u.sender_id) !== String(activeChatUserId)
        );
      }

      setUnreadBySender(unread);
    } catch (err) {
      if (![401, 404].includes(err.response?.status)) {
        console.error("❌ Error al cargar mensajes no leídos:", err);
      }
    }
  }, [isAuthenticated, activeChatUserId]);

  useEffect(() => {
    if (!isAuthenticated) return;

    fetchNotificaciones();
    fetchUnreadMessages();

    const interval = setInterval(() => {
      fetchNotificaciones();
      fetchUnreadMessages();
    }, 15 * 60 * 1000); // 15 minutos

    return () => clearInterval(interval);
  }, [isAuthenticated, fetchNotificaciones, fetchUnreadMessages]);

  useEffect(() => {
    const remove = addListener((data) => {
      if (data?.type !== "notification") return;

      const { type, from, sender_id, message_preview } = data.data;
  console.log("🔔 Tipo:", type);
      switch (type) {
        case "new_message":
          if (String(sender_id) === String(activeChatUserId)) {
            console.log("🟡 Ignorado: chat ya activo");
            return;
          }
          notify(t("notifications.new_message", { from }), "default");

          setUnreadBySender((prev) => {
  	    const existing = prev.find((u) => Number(u.sender_id) === Number(sender_id));
    	    if (existing) {
      		return prev.map((u) =>
        		Number(u.sender_id) === Number(sender_id)
          			? { ...u, count: u.count + 1 }
          			: u
      		);
	    } else {
      	     return [...prev, { sender_id, count: 1 }];
    	    }
  	  });

          fetchUnreadMessages();
          break;

        case "user_logged_out":
          notify(`${from} ha cerrado sesión`, "default");
          break;

        case "new_friend_request":
          notify(t("notifications.new_friend_request", { from }), "default");
          fetchNotificaciones();
          break;

        case "friend_request_accepted":
          notify(t("notifications.friend_request_accepted", { from }), "success");
          break;

        case "friend_request_rejected":
          notify(t("notifications.friend_request_rejected", { from }), "error");
          break;

        case "friendship_cancelled":
          notify(t("notifications.friendship_cancelled", { from }), "error");
          break;

        default:
          console.warn("🔔 Tipo de notificación no manejado:", type, data.data);
      }
    });

    return () => remove();
  }, [activeChatUserId, fetchUnreadMessages, fetchNotificaciones, addListener, t]);

  return (
    <NotificationContext.Provider
      value={{
        notify,
        notificaciones,
        setNotificaciones,
        fetchNotificaciones,
        unreadBySender,
        fetchUnreadMessages,
        activeChatUserId,
        setActiveChatUserId,
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
};
